import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PickupZone } from "@/types/api/api-types";
import { useCreatePickupZone, useUpdatePickupZone } from "@/api/pickupZones.api";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildDefaultValues,
  PickupZoneFormData,
  PickupZoneSchema,
} from "@/types/forms/bo/pickupZone.form";
import { CollectionZonePickupDays } from "./collectionZonePickupDays";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const FORM_ID = "collection-zone-form";

type CollectionEditModalProps = {
  zone: PickupZone | null;
  open: boolean;
  onClose: () => void;
};

export const CollectionZoneEditModal = ({ zone, open, onClose }: CollectionEditModalProps) => {
  const { t } = useTranslation(["boLogistics", "common"]);
  const { mutateAsync: createZone, isPending: isCreating } = useCreatePickupZone();
  const { mutateAsync: updateZone, isPending: isUpdating } = useUpdatePickupZone();
  const isSaving = isCreating || isUpdating;

  const { control, register, handleSubmit, watch, reset } = useForm<PickupZoneFormData>({
    resolver: zodResolver(PickupZoneSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: buildDefaultValues(zone),
  });

  useEffect(() => {
    if (open) reset(buildDefaultValues(zone));
  }, [open, zone, reset]);

  const onSubmit: SubmitHandler<PickupZoneFormData> = async (data) => {
    if (zone) await updateZone({ data, id: zone.id });
    else await createZone(data);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!isSaving}
      title={zone ? t("zoneEditModal.editTitle") : t("zoneEditModal.createTitle")}
      footer={
        <div className="flex w-full flex-row items-center gap-4">
          <Button type="submit" form={FORM_ID} disabled={isSaving} className="w-full">
            {isSaving ? <Loader2 className="animate-spin" /> : t("zoneEditModal.submit")}
          </Button>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={isSaving}
            className="w-full"
          >
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col justify-center gap-4"
      >
        <div className="flex w-full flex-row items-center gap-4">
          <ControlledUnifiedInput
            label={t("zoneEditModal.nameLabel")}
            control={control}
            name="name"
            wrapperClassName="w-full"
          />
          <input
            {...register("color")}
            type="color"
            aria-label={t("zoneEditModal.colorLabel")}
            className="h-9 w-9 cursor-pointer rounded"
          />
        </div>
        <ControlledUnifiedInput
          label={t("zoneEditModal.maxPickupLabel")}
          control={control}
          name="maxPickup"
          wrapperClassName="w-full"
          inputMode="numeric"
          inputType="integer"
        />
        <CollectionZonePickupDays watch={watch} control={control} />
        <ControlledCheckbox
          control={control}
          name="remote"
          label={t("zoneEditModal.remoteLabel")}
        />
      </form>
    </Modal>
  );
};
